// session-lifecycle.store.ts
import { DOCUMENT } from '@angular/common';
import { computed, effect, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { patchState, signalStore, withComputed, withHooks, withMethods, withProps, withState } from '@ngrx/signals';
import { SessionExtendEndpoint } from '@clinic-endpoints';
import { LoggedInUserStore } from './logged-in-user-store.store';

const TOKEN_LIFETIME_MIN = 16;
const DEFAULT_SESSION_DURATION_MS = TOKEN_LIFETIME_MIN * 60 * 1000; // 16 min
const WARNING_BEFORE_EXPIRY_MIN = 3;
const DEFAULT_WARNING_BEFORE_EXPIRY_MS = WARNING_BEFORE_EXPIRY_MIN * 60 * 1000; // 3 min

type SessionLifecycleState = {
  // timing config
  sessionDurationMs: number;
  warningBeforeExpiryMs: number;

  // lifecycle
  expiresAtMs: number | null;
  showExtendModal: boolean;
  isExpired: boolean;

  // status
  extendInFlight: boolean;
  extendError: unknown | null;
};

export const SessionLifecycleStore = signalStore(
  { providedIn: 'root' },
  withProps(() => ({
    _windowRef: inject(DOCUMENT).defaultView,
    _extendEndpoint: new SessionExtendEndpoint(),

    // internal timer handles (these ARE writable signals)
    _tickIntervalId: signal<number | undefined>(undefined),
    _warningTimeoutId: signal<number | undefined>(undefined),
    _expiryTimeoutId: signal<number | undefined>(undefined),

    // internal "now" signal (for countdown)
    _nowMs: signal<number>(Date.now()),
  })),

  withState<SessionLifecycleState>({
    sessionDurationMs: DEFAULT_SESSION_DURATION_MS,
    warningBeforeExpiryMs: DEFAULT_WARNING_BEFORE_EXPIRY_MS,

    expiresAtMs: null,
    showExtendModal: false,
    isExpired: false,

    extendInFlight: false,
    extendError: null,
  }),

  withComputed(store => {
    const remainingMs = computed(() => {
      const exp = store.expiresAtMs();
      if (!exp) return 0;
      return Math.max(0, exp - store._nowMs());
    });

    const remainingSeconds = computed(() => Math.ceil(remainingMs() / 1000));

    const isInWarningWindow = computed(() => {
      const exp = store.expiresAtMs();
      if (!exp) return false;
      const ms = remainingMs();
      return ms > 0 && ms <= store.warningBeforeExpiryMs();
    });

    const remainingSecondsUntilWarning = computed(() => {
      if (!store.expiresAtMs()) return 0;
      const msUntilWarning = remainingMs() - store.warningBeforeExpiryMs();
      return Math.ceil(Math.max(0, msUntilWarning) / 1000);
    });

    return {
      remainingMs,
      remainingSeconds,
      isInWarningWindow,
      remainingSecondsUntilWarning,
    };
  }),

  withMethods(store => {
    const clearTimeoutSafe = (id: number | undefined) => {
      if (id !== undefined) store._windowRef?.clearTimeout(id);
    };

    const clearIntervalSafe = (id: number | undefined) => {
      if (id !== undefined) store._windowRef?.clearInterval(id);
    };

    const stopTimers = () => {
      clearTimeoutSafe(store._warningTimeoutId());
      clearTimeoutSafe(store._expiryTimeoutId());
      clearIntervalSafe(store._tickIntervalId());

      store._warningTimeoutId.update(() => undefined);
      store._expiryTimeoutId.update(() => undefined);
      store._tickIntervalId.update(() => undefined);
    };

    const startTick = () => {
      clearIntervalSafe(store._tickIntervalId());
      const id = store._windowRef?.setInterval(() => {
        store._nowMs.update(() => Date.now());
        const exp = store.expiresAtMs();
        // future debug helper
        // if (exp) {
        //   const now = Date.now();
        //   const msToExpiry = Math.max(0, exp - now);
        //   const msToWarning = Math.max(0, msToExpiry - store.warningBeforeExpiryMs());
        //   console.log('Session countdown:', {
        //     secondsUntilWarning: Math.ceil(msToWarning / 1000),
        //     secondsUntilExpiry: Math.ceil(msToExpiry / 1000),
        //   });
        // }
      }, 1000);
      if (id !== undefined) store._tickIntervalId.update(() => id);
    };

    const armTimers = () => {
      stopTimers();

      const exp = store.expiresAtMs();
      if (!exp) return;

      const now = Date.now();
      const msToExpiry = exp - now;

      // already expired
      if (msToExpiry <= 0) {
        patchState(store, { showExtendModal: false, isExpired: true });
        return;
      }

      startTick();

      const msToWarning = msToExpiry - store.warningBeforeExpiryMs();

      const warnId = store._windowRef?.setTimeout(
        () => {
          patchState(store, { showExtendModal: true });
        },
        Math.max(0, msToWarning),
      );

      const expId = store._windowRef?.setTimeout(() => {
        patchState(store, { showExtendModal: false, isExpired: true });
      }, msToExpiry);

      if (warnId !== undefined) store._warningTimeoutId.update(() => warnId);
      if (expId !== undefined) store._expiryTimeoutId.update(() => expId);
    };

    const computeNextExpiry = () => {
      return Date.now() + store.sessionDurationMs();
    };

    const resetState = () => {
      patchState(store, {
        expiresAtMs: null,
        showExtendModal: false,
        isExpired: false,
        extendInFlight: false,
        extendError: null,
      });
    };

    return {
      // configure timings (optional)
      configure: (cfg: Partial<Pick<SessionLifecycleState, 'sessionDurationMs' | 'warningBeforeExpiryMs'>>) => {
        patchState(store, {
          ...(cfg.sessionDurationMs != null ? { sessionDurationMs: cfg.sessionDurationMs } : null),
          ...(cfg.warningBeforeExpiryMs != null ? { warningBeforeExpiryMs: cfg.warningBeforeExpiryMs } : null),
        });

        armTimers();
      },

      // start session countdown from now (or restart)
      start: () => {
        patchState(store, {
          isExpired: false,
          extendError: null,
          showExtendModal: false,
          expiresAtMs: Date.now() + store.sessionDurationMs(),
        });

        armTimers();
      },

      // stop + reset everything (logout/token removed)
      stop: () => {
        stopTimers();
        resetState();
      },

      // if token store wants to set expiry explicitly (optional)
      setExpiresAtMs: (expiresAtMs: number | null) => {
        patchState(store, {
          expiresAtMs,
          isExpired: false,
          showExtendModal: false,
        });

        armTimers();
      },

      // close the modal without extending
      dismissModal: () => patchState(store, { showExtendModal: false }),

      // call API to extend + push expiry forward on success
      extend: () => {
        if (store.extendInFlight()) return;

        patchState(store, { extendInFlight: true, extendError: null });

        store._extendEndpoint.put().subscribe({
          next: () => {
            patchState(store, {
              extendInFlight: false,
              showExtendModal: false,
              isExpired: false,
              expiresAtMs: computeNextExpiry(),
            });

            armTimers();
          },
          error: err => {
            patchState(store, { extendInFlight: false, extendError: err });
          },
        });
      },

      // token store can call after it handled expiration
      acknowledgeExpired: () => patchState(store, { isExpired: false }),

      // (optional) call this if you ever need to re-arm manually
      _armTimers: armTimers,
    };
  }),
  withHooks({
    onInit(store) {
      const router = inject(Router);
      const loggedInUserStore = inject(LoggedInUserStore);

      effect(() => {
        if (store.isExpired()) {
          store.stop();
          loggedInUserStore.logout();
          router.navigate(['/login']);
        }
      });
    },
  }),
);
